import React, { useMemo } from 'react';
import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';

import { useTranslation } from '@shared/core';
import { CarNameSearch } from './CarNameSearch';
import { CheckboxFacet } from './CheckboxFacet';
import { PriceRangeFacet } from './PriceRangeFacet';
import { MobileSortOptions } from './MobileSortOptions';
import { SpecificationsFacet } from './SpecificationsFacet';
import type { CarFiltersPanelProps } from './CarFiltersPanel.types';
import { PASSENGER_BUCKETS } from '../../CarRentalConsole.constants';
import { useCarAttributeLabels } from '../../hooks/useCarAttributeLabels';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

const Container = ({ children }: Readonly<{ children?: React.ReactNode }>) => (
  <Flex
    rowGap={6}
    padding={6}
    flexDirection="column"
    width={{ _: 'full', md: pxToRem(292) }}
    maxWidth={{ _: 'full', md: pxToRem(292) }}
  >
    {children}
  </Flex>
);

const toggleValue = <T,>(source: ReadonlyArray<T>, value: T): ReadonlyArray<T> => (source.includes(value) ? source.filter(item => item !== value) : [...source, value]);

const MobileFilterActions = ({ onReset, onClose }: Readonly<{ onReset?: () => void; onClose?: () => void }>) => {
  const { tq } = useTranslation('carRentalConsole');

  return (
    <Flex
      gap={4}
      marginTop={6}
      zIndex={2}
      width="100%"
      position="sticky"
      bottom={0}
      background="white"
      padding={4}
      borderTop="1px solid"
      borderTopColor="mono3"
    >
      <ButtonV2
        variant="ghost"
        intent="neutral"
        width="50%"
        onClick={onReset}
      >
        {tq`Reset All Filters`}
      </ButtonV2>
      <ButtonV2
        variant="solid"
        intent="neutral"
        shape="rounded"
        width="50%"
        onClick={onClose}
      >
        {tq`Show Results`}
      </ButtonV2>
    </Flex>
  );
};

export const CarFiltersPanel = ({
  onClose,
  onReset,
  filters,
  eventName,
  priceRange,
  sortOption,
  onSortChange,
  currencyCode,

  carTypeCounts,
  companyCounts,
  carNameSearch,
  fuelTypeCounts,
  onFiltersChange,
  dynamicPriceMax,
  driveTypeCounts,
  navigatorLanguage,
  supportedCarTypes,
  supportedCompanies,
  onPriceRangeChange,
  supportedFuelTypes,
  transmissionCounts,
  supportedDriveTypes,
  onCarNameSearchChange,
  passengerBucketCounts,
  supportedTransmissions,
  cancellationTypeCounts,
  supportedCancellationTypes,
}: CarFiltersPanelProps) => {
  const { tq } = useTranslation('carRentalConsole');
  const { formatSpecification, formatVehicleCategory, formatCancellationType } = useCarAttributeLabels();

  const isMobileOrTablet = useIsMobileOrTablet();
  const passengerOptions = useMemo(() => PASSENGER_BUCKETS.map(String), []);

  return (
    <>
      <Container>
        <Flex justifyContent="space-between">
          {isMobileOrTablet || !eventName ? (
            <TextV2 typographyVariant="hed4">{tq`Filters`}</TextV2>
          ) : (
            <Flex
              flexDirection="column"
              gap={2}
            >
              <TextV2
                fontWeight={400}
                fontFamily="Forum"
                fontSize={pxToRem(24)}
              >
                {eventName}
              </TextV2>
              <TextV2 typographyVariant="label2">{tq`Browse rental cars with exclusive rates for our guests.`}</TextV2>
            </Flex>
          )}
          {isMobileOrTablet && (
            <Close
              cursor="pointer"
              onClick={onClose}
              aria-label={tq`Close filters`}
            />
          )}
        </Flex>
        <CarNameSearch
          value={carNameSearch}
          onChange={onCarNameSearchChange}
        />
        <PriceRangeFacet
          priceRange={priceRange}
          currencyCode={currencyCode}
          dynamicPriceMax={dynamicPriceMax}
          navigatorLanguage={navigatorLanguage}
          onPriceRangeChange={onPriceRangeChange}
        />
        {isMobileOrTablet && (
          <MobileSortOptions
            sortOption={sortOption}
            onSortChange={onSortChange}
          />
        )}
        <CheckboxFacet
          title={tq`Car type`}
          counts={carTypeCounts}
          options={supportedCarTypes}
          selected={filters.carTypes}
          formatLabel={formatVehicleCategory}
          onToggle={value => onFiltersChange({ ...filters, carTypes: toggleValue(filters.carTypes, value) })}
        />
        <CheckboxFacet
          title={tq`Cancellation`}
          counts={cancellationTypeCounts}
          formatLabel={formatCancellationType}
          selected={filters.cancellationTypes}
          options={supportedCancellationTypes}
          onToggle={value => onFiltersChange({ ...filters, cancellationTypes: toggleValue(filters.cancellationTypes, value) })}
        />
        <CheckboxFacet
          title={tq`Capacity`}
          options={passengerOptions}
          counts={passengerBucketCounts}
          formatLabel={value => tq`${value}+ passengers`}
          selected={filters.passengerBuckets.map(String)}
          onToggle={value => onFiltersChange({ ...filters, passengerBuckets: toggleValue(filters.passengerBuckets, parseInt(value, 10)) })}
        />
        <CheckboxFacet
          counts={companyCounts}
          selected={filters.companies}
          title={tq`Car hire company`}
          options={supportedCompanies}
          onToggle={value => onFiltersChange({ ...filters, companies: toggleValue(filters.companies, value) })}
        />
        <SpecificationsFacet
          filters={filters}
          fuelTypeCounts={fuelTypeCounts}
          driveTypeCounts={driveTypeCounts}
          onFiltersChange={onFiltersChange}
          supportedFuelTypes={supportedFuelTypes}
          transmissionCounts={transmissionCounts}
          supportedDriveTypes={supportedDriveTypes}
          formatSpecification={formatSpecification}
          supportedTransmissions={supportedTransmissions}
        />
      </Container>

      {isMobileOrTablet && (
        <MobileFilterActions
          onReset={onReset}
          onClose={onClose}
        />
      )}
    </>
  );
};
